import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Search, ShoppingBag } from "lucide-react";
import FoodImage from '../../components/FoodImage';
import { api, errMsg, inr } from "../../lib/api";
import { useLive } from "../../lib/live";
import { VegMark } from "../../components/VegMark";
import ItemSheet from "./ItemSheet";
import CartSheet, { flushPending } from "./CartSheet";
import { OfflineBanner } from "../../components/Offline";
import { t, tr, LANG_NAMES, registerLangs } from "../../lib/i18n";
import CustomerReviews from './CustomerReviews';

const FILTERS = [["all", "All"], ["combo", "Combos"], ["veg", "Veg"], ["non_veg", "Non-veg"], ["egg", "Egg"]];

function useCart(qr) {
  const key = `mbp_cart_${qr}`;
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem(key) || "[]"));
  useEffect(() => localStorage.setItem(key, JSON.stringify(cart)), [cart, key]);
  return [cart, setCart];
}

export default function GuestMenu() {
  const { qr } = useParams();
  const nav = useNavigate();
  const [menu, setMenu] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [cat, setCat] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useCart(qr);
  const [lang, setLang] = useState(localStorage.getItem("mbp_lang") || "en");
  const [customerSignedIn,setCustomerSignedIn]=useState(false);
  useEffect(()=>{
    let active=true;
    const refresh=()=>api.get('/customer/me').then(({data})=>{if(active)setCustomerSignedIn(!!data.customer);}).catch(()=>{if(active)setCustomerSignedIn(false);});
    const onVisible=()=>{if(!document.hidden)refresh();};
    refresh();
    window.addEventListener('focus',refresh);
    window.addEventListener('customer-auth-changed',refresh);
    document.addEventListener('visibilitychange',onVisible);
    return()=>{active=false;window.removeEventListener('focus',refresh);window.removeEventListener('customer-auth-changed',refresh);document.removeEventListener('visibilitychange',onVisible);};
  },[]);
  useEffect(() => localStorage.setItem("mbp_lang", lang), [lang]);

  const loadMenu = useCallback(() => api.get(`/public/menu/${qr}`).then((r) => { registerLangs(r.data.outlet.ui_i18n, r.data.outlet.language_names); setMenu(r.data); }).catch((e) => setErr(errMsg(e))), [qr]);
  useEffect(() => { loadMenu(); }, [loadMenu]);
  useEffect(() => {
    const go = () => flushPending(qr).then((res) => res && placed(res));
    go();
    window.addEventListener("online", go);
    return () => window.removeEventListener("online", go);
  }, [qr]); // eslint-disable-line react-hooks/exhaustive-deps
  const placed = (res) => {
    setCart([]);
    const t = res.guest_token;
    localStorage.setItem(`mbp_order_${res.order.id}`, t);
    localStorage.setItem(`mbp_last_order_${qr}`, JSON.stringify({ id: res.order.id, t }));
    if (res.checkout_url) nav(res.checkout_url);
    else nav(`/order/${res.order.id}?t=${t}`);
  };

  const live = useLive(menu ? `menu:${menu.outlet.id}` : null, async () => (await api.get(`/public/menu/${qr}/availability`)).data, {
    pollMs: 4000,
    onEvent: (m) => m.event !== "availability.changed" && loadMenu(),
  });
  const soldOut = useMemo(() => new Set(live.data?.sold_out || []), [live.data]);

  const items = useMemo(() => {
    if (!menu) return [];
    const s = q.trim().toLowerCase();
    return menu.items.filter((i) => (filter === "all" || (filter === 'combo' ? i.combo_item_ids?.length > 0 : i.veg_flag === filter)) && (!s || i.name.toLowerCase().includes(s) || (i.description || "").toLowerCase().includes(s)));
  }, [menu, q, filter]);

  useEffect(() => {
    if (cart.some((c) => soldOut.has(c.item_id))) setCart((c) => c.filter((x) => !soldOut.has(x.item_id)));
  }, [soldOut, cart, setCart]);

  if (err) return <GuestError msg={err} />;
  if (!menu) return <MenuSkeleton />;

  const count = cart.reduce((a, c) => a + c.qty, 0);
  const subtotal = cart.reduce((a, c) => a + c.unit * c.qty, 0);
  const cats = menu.categories.filter((c) => items.some((i) => i.category_id === c.id));
  const jump = (id) => { setCat(id); document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div className="mx-auto min-h-screen max-w-md bg-[#FAF6F0] pb-28 text-stone-900" data-testid="guest-menu-page">
      <OfflineBanner note="You can keep browsing; your order will be sent when you reconnect." />
      <header className="grain relative overflow-hidden bg-[#1C1917] px-5 pb-6 pt-7 text-[#FAF6F0]">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#FF9D05] opacity-90" />
        <p className="relative font-mono text-[11px] uppercase tracking-[0.2em] text-orange-200" data-testid="guest-table-label">
          {menu.table.kind} {menu.table.code}
        </p>
        <h1 className="relative mt-1 text-4xl font-extrabold tracking-tight" data-testid="guest-outlet-name">{menu.outlet.name}</h1>
        <p className="relative mt-1 text-sm text-stone-300">{t(lang, "tagline")}</p>
        <Link to={`/customer?qr=${encodeURIComponent(qr)}`} className="relative mt-3 inline-flex min-h-11 items-center rounded-full border border-stone-400 px-4 text-sm font-semibold">{customerSignedIn?'My account & rewards':'Sign in / Create account'}</Link>
        {menu.outlet.languages?.length > 0 && (
          <select data-testid="guest-language-select" value={lang} onChange={(e) => setLang(e.target.value)} className="relative mt-3 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white">
            {["en", ...menu.outlet.languages].map((l) => <option key={l} value={l} className="text-black">{LANG_NAMES[l] || l}</option>)}
          </select>
        )}
      </header>

      <div className="sticky top-0 z-20 space-y-3 border-b border-stone-200 bg-[#FAF6F0]/95 px-4 py-3 backdrop-blur-md">
        <label className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2.5">
          <Search className="h-4 w-4 text-stone-400" />
          <input data-testid="menu-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t(lang, "search")} className="w-full bg-transparent text-sm outline-none" />
        </label>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${filter === k ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 bg-white text-stone-700"}`}>
              {k !== "all" && k !== 'combo' && <VegMark flag={k} size={11} />} {k === 'combo' ? l : t(lang, k) || l}
            </button>
          ))}
        </div>
        <div className="flex gap-4 overflow-x-auto no-scrollbar text-sm">
          {cats.map((c) => (
            <button key={c.id} data-testid={`category-tab-${c.id}`} onClick={() => jump(c.id)}
              className={`shrink-0 border-b-2 pb-1 font-semibold transition-colors ${cat === c.id ? "border-[#FF9D05] text-[#FF9D05]" : "border-transparent text-stone-500"}`}>
              {tr(c, lang, "name") || c.name}
            </button>
          ))}
        </div>
      </div>

      <main className="px-4">
        {cats.length === 0 && <p className="py-16 text-center text-sm text-stone-500" data-testid="menu-empty">No dishes match your search.</p>}
        {cats.map((c) => (
          <section key={c.id} id={`cat-${c.id}`} className="scroll-mt-40 pt-6">
            <h2 className="mb-3 text-lg font-bold tracking-tight">{typeof c.i18n?.[lang] === "string" ? c.i18n[lang] : c.name}{!c.available_now && <span className="ml-2 text-xs font-semibold text-red-600" data-testid={`category-unavailable-${c.id}`}>· {t(lang, "unavailable")} ({c.available_from}–{c.available_to})</span>}</h2>
            <div className="space-y-3">
              {items.filter((i) => i.category_id === c.id).map((i, idx) => (
                <ItemCard key={i.id} item={i} included={menu.items.filter(included => i.combo_item_ids?.includes(included.id))} lang={lang} soldOut={soldOut.has(i.id) || !c.available_now} delay={idx * 40} onOpen={() => setSheet(i)} />
              ))}
            </div>
          </section>
        ))}
        <CustomerReviews qr={qr}/>
      </main>

      {count > 0 && (
        <button data-testid="open-cart-button" onClick={() => setCartOpen(true)}
          className="fixed bottom-4 left-1/2 z-30 flex w-[calc(100%-2rem)] max-w-[26rem] -translate-x-1/2 items-center justify-between rounded-2xl bg-[#FF9D05] px-5 py-4 text-white shadow-xl shadow-orange-900/25 transition-transform active:scale-[0.98]">
          <span className="flex items-center gap-2 font-semibold">
            <ShoppingBag className="h-5 w-5" />
            <span key={count} className="animate-pop" data-testid="cart-count">{count} item{count > 1 ? "s" : ""}</span>
          </span>
          <span className="font-display text-lg font-bold" data-testid="cart-subtotal">{inr(subtotal)} →</span>
        </button>
      )}

      <ItemSheet item={sheet} items={menu.items} groups={menu.modifier_groups} onClose={() => setSheet(null)}
        onAdd={(line) => { setCart((c) => [...c, line]); setSheet(null); }} />
      <CartSheet open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} setCart={setCart} menu={menu} qr={qr}
        onPlaced={placed} />
      <LastOrderLink qr={qr} />
    </div>
  );
}

export function ItemCard({ item, included = [], soldOut, unavailableLabel, onOpen, delay, lang }) {
  return (
    <button data-testid={`menu-item-${item.id}`} disabled={soldOut||!!unavailableLabel} onClick={onOpen} style={{ animationDelay: `${delay}ms` }}
      className={`rise flex w-full gap-3 rounded-2xl border border-stone-200 bg-white p-3 text-left transition-shadow hover:shadow-md ${soldOut||unavailableLabel ? "opacity-60" : ""}`}>
      <div className="min-w-0 flex-1">
        <VegMark flag={item.veg_flag} />
        {item.combo_item_ids?.length > 0 && <span className="ml-2 inline-flex rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800" data-testid={`combo-badge-${item.id}`}>Combo</span>}
        <h3 className="mt-1.5 text-[15px] font-semibold leading-tight">{tr(item, lang, "name")}</h3>
        <p className="mt-1 font-display font-bold" data-testid={`menu-item-price-${item.id}`}>{inr(item.price_paise)}</p>
        <p className="mt-1 line-clamp-2 text-xs text-stone-500">{tr(item, lang, "description")}</p>
        {unavailableLabel&&<p className="mt-2 text-xs font-semibold text-amber-700">{unavailableLabel}</p>}
        {included.length > 0 && <p className="mt-2 text-xs font-medium text-stone-700" data-testid={`combo-includes-${item.id}`}>Includes: {included.map(i => tr(i, lang, 'name')).join(' + ')}</p>}
      </div>
      <div className="relative h-28 w-28 shrink-0">
        <FoodImage src={item.image_url} alt={item.name} loading="lazy" className="h-full w-full rounded-xl object-cover"/>
        <span data-testid={soldOut ? `sold-out-badge-${item.id}` : `add-item-${item.id}`}
          className={`absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg border px-4 py-1 text-xs font-bold shadow-sm ${soldOut||unavailableLabel ? "border-stone-300 bg-stone-100 text-stone-500" : "border-[#FF9D05] bg-white text-[#FF9D05]"}`}>
          {soldOut ? t(lang, "sold_out") : unavailableLabel?'Unavailable':t(lang, "add")}
        </span>
      </div>
    </button>
  );
}

function LastOrderLink({ qr }) {
  const last = JSON.parse(localStorage.getItem(`mbp_last_order_${qr}`) || "null");
  if (!last) return null;
  return (
    <a href={`/order/${last.id}?t=${last.t}`} data-testid="last-order-link" className="mx-4 mt-8 block text-center text-sm font-semibold text-[#FF9D05] underline">
      Track your last order
    </a>
  );
}

export function GuestError({ msg }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[#FAF6F0] p-8 text-center" data-testid="guest-error">
      <div>
        <h1 className="text-3xl font-extrabold">Hmm, that didn't work</h1>
        <p className="mt-2 text-stone-600">{msg}</p>
        <button onClick={() => window.location.reload()} className="mt-6 rounded-full bg-stone-900 px-6 py-3 text-sm font-semibold text-white" data-testid="guest-error-retry">Try again</button>
      </div>
    </div>
  );
}

function MenuSkeleton() {
  return (
    <div className="mx-auto max-w-md animate-pulse space-y-4 bg-[#FAF6F0] p-4" data-testid="menu-loading">
      <div className="h-32 rounded-2xl bg-stone-200" />
      {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 rounded-2xl bg-stone-200" />)}
    </div>
  );
}
